import React, { useState, useEffect, useCallback, useMemo } from "react";
import BackButton from "../../shared/BackButton/BackButton";
import LanguageControlsGrid, {
  AVAILABLE_LANGUAGES,
} from "../../shared/LanguageControlsGrid/LanguageControlsGrid";
import styles from "./TranslationMediator.module.css";

import { fetchVocabularyWords } from "../../API/Vocabulary/VocabularyAPI";
import { callOpenAI } from "../../API/API";
import {
  fetchTranslationMediatorExercises,
  createTranslationMediatorExercise,
  deleteTranslationMediatorExercise,
  TranslationMediatorExercise,
  TranslationMediatorTurn,
} from "../../API/Lesson/TranslationMediatorAPI";
import { buildTranslationMediatorPrompt } from "../../GenAIPrompts/GenAIPrompts";

const LANG_CODE_MAP: Record<string, string> = {
  English: "EN",
  Spanish: "ES",
  Italian: "IT",
  French: "FR",
  German: "DE",
  Portuguese: "PT",
};

const LANG_NAME_MAP: Record<string, string> = {
  EN: "English",
  ES: "Spanish",
  IT: "Italian",
  FR: "French",
  DE: "German",
  PT: "Portuguese",
};

function generateUUID() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

const TranslationMediatorManager: React.FC = () => {
  // Controls state
  const [sourceLanguage, setSourceLanguage] = useState<string>("");
  const [baseLanguage, setBaseLanguage] = useState<string>("EN");
  const [section, setSection] = useState<string>("");
  const [domain, setDomain] = useState<string>("general");
  const [proficiencyLevel, setProficiencyLevel] = useState<string>("B1");
  const [difficulty, setDifficulty] = useState<string>("2");

  // Data state
  const [vocabularyWords, setVocabularyWords] = useState<string[]>([]);
  const [selectedWords, setSelectedWords] = useState<Set<string>>(new Set());
  const [existingExercises, setExistingExercises] = useState<TranslationMediatorExercise[]>([]);
  const [aiPreview, setAiPreview] = useState<TranslationMediatorExercise | null>(null);

  // Loading state
  const [loadingExisting, setLoadingExisting] = useState<boolean>(false);
  const [generatingAi, setGeneratingAi] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [aiError, setAiError] = useState<string | null>(null);

  const langCodeMap = useMemo(() => LANG_CODE_MAP, []);

  // Load vocabulary when section changes
  useEffect(() => {
    if (!section) return;
    setError(null);
    setVocabularyWords([]);
    setSelectedWords(new Set());
    fetchVocabularyWords(section)
      .then((ws) => {
        const uniq = Array.from(new Set(ws.map((w) => w.Targ_Word?.trim()).filter(Boolean)));
        setVocabularyWords(uniq.sort());
      })
      .catch((err) => setError(err.message || String(err)));
  }, [section]);

  // Load existing exercises when section changes
  useEffect(() => {
    if (!section) return;
    setLoadingExisting(true);
    setError(null);
    fetchTranslationMediatorExercises(section)
      .then((items) => setExistingExercises(items))
      .catch((err) => setError(err.message || String(err)))
      .finally(() => setLoadingExisting(false));
  }, [section]);

  // Handlers
  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSourceLanguage(e.target.value);
    setSection("");
  };

  const toggleWord = (w: string) => {
    setSelectedWords((prev) => {
      const nxt = new Set(prev);
      nxt.has(w) ? nxt.delete(w) : nxt.add(w);
      return nxt;
    });
  };

  const handleGenerate = async () => {
    if (!selectedWords.size || !sourceLanguage) return;

    const targLangCode = langCodeMap[sourceLanguage];
    const targLangName = sourceLanguage;
    const baseLangName = LANG_NAME_MAP[baseLanguage] || "English";

    const payload = buildTranslationMediatorPrompt(
      Array.from(selectedWords),
      targLangName,
      targLangCode,
      baseLangName,
      baseLanguage,
      proficiencyLevel,
      difficulty,
      section,
      4
    );

    setGeneratingAi(true);
    setAiError(null);
    setAiPreview(null);

    try {
      const apiRes = await callOpenAI(payload);
      let parsed: any;

      if (typeof apiRes?.response === "string") {
        let jsonStr = apiRes.response;
        const start = jsonStr.indexOf("{");
        const end = jsonStr.lastIndexOf("}") + 1;
        if (start !== -1 && end > start) {
          jsonStr = jsonStr.slice(start, end);
        }
        parsed = JSON.parse(jsonStr);
      } else {
        parsed = apiRes?.response;
      }

      if (!parsed?.turns || !Array.isArray(parsed.turns)) {
        throw new Error("AI response missing 'turns' array");
      }

      const exercise: TranslationMediatorExercise = {
        sectionId: section,
        exerciseId: generateUUID(),
        conversationTopic: parsed.conversationTopic || "",
        baseLanguage: baseLanguage,
        targetLanguage: targLangCode,
        proficiencyLevel,
        difficulty,
        avatarAName: parsed.avatarAName || "Speaker A",
        avatarBName: parsed.avatarBName || "Speaker B",
        avatarAGender: parsed.avatarAGender || "male",
        avatarBGender: parsed.avatarBGender || "female",
        turns: parsed.turns.map((t: any, i: number) => ({
          turnIndex: t.turnIndex ?? i,
          speaker: t.speaker,
          originalText: t.originalText,
          expectedTranslation: t.expectedTranslation,
          hintText: t.hintText || t.expectedTranslation,
        })),
      };

      setAiPreview(exercise);
    } catch (err: any) {
      setAiError(err.message || String(err));
    } finally {
      setGeneratingAi(false);
    }
  };

  const handleSave = async () => {
    if (!aiPreview) return;
    setIsSaving(true);
    try {
      await createTranslationMediatorExercise(aiPreview);
      setExistingExercises((prev) => [aiPreview, ...prev]);
      setAiPreview(null);
    } catch (err: any) {
      setAiError(err.message || String(err));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (sectionId: string, exerciseId: string) => {
    if (!window.confirm("Delete this exercise?")) return;
    try {
      await deleteTranslationMediatorExercise(sectionId, exerciseId);
      setExistingExercises((prev) => prev.filter((e) => e.exerciseId !== exerciseId));
    } catch (err: any) {
      alert("Failed to delete: " + (err.message || String(err)));
    }
  };

  // Render a conversation card
  const renderConversationCard = (
    exercise: TranslationMediatorExercise,
    isPreview: boolean = false
  ) => (
    <div
      className={styles.conversationCard}
      key={exercise.exerciseId}
      style={isPreview ? { border: "2px solid #4caf50" } : undefined}
    >
      <div className={styles.conversationHeader}>
        <span className={styles.conversationTopic}>
          {exercise.conversationTopic || "Untitled Conversation"}
        </span>
        <span className={styles.conversationMeta}>
          {exercise.baseLanguage} / {exercise.targetLanguage} | {exercise.proficiencyLevel} |{" "}
          {exercise.turns?.length || 0} turns
        </span>
      </div>

      <div className={styles.avatarInfo}>
        <span className={`${styles.avatarBadge} ${styles.avatarA}`}>
          {exercise.avatarAName} ({exercise.avatarAGender}) -{" "}
          {LANG_NAME_MAP[exercise.baseLanguage] || exercise.baseLanguage}
        </span>
        <span className={`${styles.avatarBadge} ${styles.avatarB}`}>
          {exercise.avatarBName} ({exercise.avatarBGender}) -{" "}
          {LANG_NAME_MAP[exercise.targetLanguage] || exercise.targetLanguage}
        </span>
      </div>

      <ul className={styles.turnList}>
        {(exercise.turns || []).map((turn, i) => (
          <li key={i} className={styles.turnItem}>
            <div
              className={`${styles.turnSpeaker} ${
                turn.speaker === "A" ? styles.speakerA : styles.speakerB
              }`}
            >
              {turn.speaker}
            </div>
            <div className={styles.turnContent}>
              <div className={styles.turnOriginal}>{turn.originalText}</div>
              <div className={styles.turnTranslation}>
                <span className={styles.turnArrow}>&rarr;</span>
                {turn.expectedTranslation}
              </div>
            </div>
          </li>
        ))}
      </ul>

      <div className={styles.cardActions}>
        {isPreview ? (
          <>
            <button className={styles.button} onClick={handleSave} disabled={isSaving}>
              {isSaving ? "Saving..." : "Save Exercise"}
            </button>
            <button
              className={styles.button}
              onClick={() => setAiPreview(null)}
              style={{ backgroundColor: "#8b99af", color: "white" }}
            >
              Discard
            </button>
            <button
              className={styles.button}
              onClick={handleGenerate}
              disabled={generatingAi}
              style={{ backgroundColor: "#ff9800", color: "white" }}
            >
              Regenerate
            </button>
          </>
        ) : (
          <button
            className={`${styles.button} ${styles.deleteButton}`}
            onClick={() => handleDelete(exercise.sectionId, exercise.exerciseId)}
          >
            Delete
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className={styles.container}>
      <BackButton />
      <h1 className={styles.title}>Translation Mediator</h1>

      <LanguageControlsGrid
        sourceLanguage={sourceLanguage}
        section={section}
        domain={domain}
        proficiencyLevel={proficiencyLevel}
        difficulty={difficulty}
        isGenerating={generatingAi}
        onSourceLanguageChange={handleLanguageChange}
        onSectionChange={(e) => setSection(e.target.value)}
        onDomainChange={(e) => setDomain(e.target.value)}
        onProficiencyChange={(e) => setProficiencyLevel(e.target.value)}
        onDifficultyChange={(e) => setDifficulty(e.target.value)}
        sectionLabel="Section:"
        error={error}
      />

      {error && <div className={styles.error}>{error}</div>}

      {/* Vocabulary word selection */}
      {section && vocabularyWords.length > 0 && (
        <div className={styles.sectionContainer}>
          <h2>Select Vocabulary Words</h2>
          <p style={{ color: "#666", fontSize: 14 }}>
            Choose words to incorporate into the conversation. The AI will generate a natural
            dialogue using these words.
          </p>
          <table className={styles.vocabTable}>
            <thead>
              <tr>
                <th style={{ width: 60 }}>Select</th>
                <th>Word</th>
              </tr>
            </thead>
            <tbody>
              {vocabularyWords.map((w, i) => (
                <tr key={i}>
                  <td style={{ textAlign: "center" }}>
                    <input
                      type="checkbox"
                      checked={selectedWords.has(w)}
                      onChange={() => toggleWord(w)}
                    />
                  </td>
                  <td>{w}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Generate button */}
      {selectedWords.size > 0 && (
        <button className={styles.button} onClick={handleGenerate} disabled={generatingAi}>
          {generatingAi
            ? "Generating Conversation..."
            : `Generate Translation Mediator (${selectedWords.size} words)`}
        </button>
      )}
      {aiError && <div className={styles.error}>{aiError}</div>}

      {/* AI Preview */}
      {aiPreview && (
        <div className={styles.sectionContainer}>
          <h2 className={styles.subHeading}>AI Generated Conversation</h2>
          {renderConversationCard(aiPreview, true)}
        </div>
      )}

      {/* Existing exercises */}
      {section && (
        <div className={styles.sectionContainer}>
          <h2 className={styles.subHeading}>Existing Exercises for &quot;{section}&quot;</h2>
          {loadingExisting && <p className={styles.infoMessage}>Loading existing exercises...</p>}
          {!loadingExisting && existingExercises.length === 0 && (
            <p className={styles.infoMessage}>
              No translation mediator exercises found for this section.
            </p>
          )}
          {!loadingExisting && existingExercises.map((ex) => renderConversationCard(ex))}
        </div>
      )}
    </div>
  );
};

export default TranslationMediatorManager;
